import React, { useCallback, useMemo, useState } from 'react';
import size from 'lodash/size';
import reduce from 'lodash/reduce';
import isEmpty from 'lodash/isEmpty';

import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesCacheKey,
  FetchLifestylesCacheKeys,
  FetchLifestylesFilterFields,
  FetchLifestylesFilterLifestyles,
  FetchLifestylesFilters,
  FetchLifestylesLimit
} from '../../../lifestyles/lifestylesTypes';
import { TeamNanoID, TeamUUID } from '../../../teams/teamsTypes';
import {
  FetchLifestylesSetByUserIdScopeType,
  FetchLifestylesSetsCacheKey,
  FetchLifestylesSetsCacheKeys
} from '../../../lifestylesSets/lifestylesSetsTypes';
import { ThreeDStockOnLifestylesSelect } from './ThreeDStockLifestyles.types';
import { FetchCacheItemSources } from '../../../../types';

import {
  FETCH_LIFESTYLES_QUERY,
  FetchLifestylesQueryResponse
} from '../../../lifestyles/queries/fetchLifestyles.query';
import { CACHE_FETCH_LIFESTYLES_QUERY } from '../../../lifestyles/queries/fetchCacheLifestyles.query';
import { CACHE_FETCH_SHOW_LIFESTYLE_QUERY } from '../../../lifestyles/queries/fetchCacheShowLifestyle.query';
import { FETCH_SHOW_LIFESTYLE_QUERY } from '../../../lifestyles/queries/fetchShowLifestyle.query';

import { useCurrentUser } from '../../../../auth/hooks/useAuth';

import { useLifestylesFiltersCache } from '../../../lifestyles/hooks/useLifestylesFiltersCache';
import { useCacheLifestyles } from '../../../lifestyles/hooks/useCacheLifestyles';
import { useThreeDStockLifestylesSearch } from './hooks/useThreeDStockLifestylesSearch';
import { useThreeDStockLifestylesContentCheckedCategories } from '../../components/contents/ThreeDStockLifestylesContent/hooks/useThreeDStockLifestylesContentCheckedCategories';
import { useLifestylesIndexPageSelectedLifestyles } from '../../../lifestyles/pages/LifestylesIndexPage/hooks/useLifestylesIndexPageSelectedLifestyles';
import { useChangePathQueryFilters } from '../../hooks/useChangePathQueryFilters';

import { ThreeDStockShowLayout } from '../../components/layouts/ThreeDStockShowLayout';

import { SelectedLifestylesSidebar } from '../../../selectedLifestyles/components/sidebars/SelectedLifestylesSidebar';
import { SelectedLifestylesFloatingCounter } from '../../../selectedLifestyles/components/SelectedLifestylesFloatingCounter';

import {
  ThreeDStockHeader,
  ThreeDStockHeaderTeamTypes
} from '../../components/headers/ThreeDStockHeader';
import { ThreeDStockSubHeader } from '../../components/headers/ThreeDStockSubHeader';
import { ThreeDStockLifestylesContent } from '../../components/contents/ThreeDStockLifestylesContent';
import { ThreeDStockMenu } from '../../components/menus/ThreeDStockMenu';
import { ThreeDStockModalHeader } from '../../components/headers/ThreeDStockModalHeader';

import { LifestyleCache } from '../../../lifestyles/LifestyleCache';
import { LifestylesPermissions } from '../../../lifestyles/lifestylesConstants';

import { StockTabs } from '../../stockConstants';
import { StockPath } from '../../StockPath';
import { TeamPath } from '../../../teams/TeamPath';

const defaultInitialLimit = 48;

const categoriesFilterFields = [
  FetchLifestylesFilterFields.CATEGORY_ID,
  FetchLifestylesFilterFields.LIFESTYLE_PARENT_CATEGORY_IDS
];

interface ThreeDStockLifestylesModalProps {
  pageAction?: never;
  isModal: boolean;
}

interface ThreeDStockLifestylesPageProps {
  pageAction: string;
  isModal?: never;
}

interface ThreeDStockLifestylesDefaultProps {
  lifestylesCacheKey: FetchLifestylesCacheKey;
  initialFilters?: FetchLifestylesFilters;
  initialLimit?: FetchLifestylesLimit;
  lifestylesSetCacheKey: FetchLifestylesSetsCacheKey;
  lifestylesSetScope?: FetchLifestylesSetByUserIdScopeType;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  companyUuid?: TeamUUID;
  onLifestylesAttach?: ThreeDStockOnLifestylesSelect;
  onLifestylesSelect?: ThreeDStockOnLifestylesSelect;
  onChangeFilters?: (filters?: FetchLifestylesFilters) => void;
  withRealAspectRatio?: boolean;
  team?: ThreeDStockHeaderTeamTypes;
}

type ThreeDStockLifestylesProps = ThreeDStockLifestylesDefaultProps &
  (ThreeDStockLifestylesModalProps | ThreeDStockLifestylesPageProps);

function ThreeDStockLifestyles({
  pageAction,
  isModal,
  lifestylesCacheKey,
  initialFilters,
  initialLimit = defaultInitialLimit,
  lifestylesSetCacheKey,
  lifestylesSetScope,
  companyNanoId,
  companyUuid,
  isMyLibrary,
  onLifestylesAttach,
  onLifestylesSelect,
  onChangeFilters,
  withRealAspectRatio,
  team
}: ThreeDStockLifestylesProps) {
  const currentUser = useCurrentUser();

  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const handleMenuToggle = useCallback<() => void>(
    () => setIsOpenMenu((prev) => !prev),
    []
  );

  const lifestylesCacheKeys = useMemo<FetchLifestylesCacheKeys>(
    () => [lifestylesCacheKey],
    [lifestylesCacheKey]
  );
  const lifestylesSetCacheKeys = useMemo<FetchLifestylesSetsCacheKeys>(
    () => [lifestylesSetCacheKey],
    [lifestylesSetCacheKey]
  );

  const {
    defaultInitialFilters,
    cachedLifestylesSort,
    cachedLifestylesFilters,
    changeCachedLifestylesFilters,
    setCachedLifestylesFilters,
    resetCachedLifestylesFiltersAndSort
  } = useLifestylesFiltersCache({
    cacheKey: lifestylesCacheKey,
    initialFilters,
    ignoredFilterNames: []
  });

  const {
    lifestyles,
    lifestylesError,
    lifestylesTotalCount,
    lifestylesFetched,
    lifestylesFetchingNextPage,
    lifestylesFilters,
    lifestylesIsPlaceholderData,
    hasNextLifestylesPage,
    updateLifestyleCache,
    loadMoreLifestyles,
    prefetchLifestyle,
    changeLifestylesFilters,
    lifestylesSort,
    sortLifestyles,
    filterLifestyles,
    lifestylesPage,
    lifestylesLimit
  } = useCacheLifestyles<FetchLifestylesQueryResponse>({
    cacheKey: lifestylesCacheKey,
    cacheQuery: CACHE_FETCH_LIFESTYLES_QUERY,
    fetchCacheItemQuery: CACHE_FETCH_SHOW_LIFESTYLE_QUERY,
    fetchItemCacheKey: LifestyleCache.showCacheKey(),
    fetchCacheItemSource: FetchCacheItemSources.ES,
    fetchItemQuery: FETCH_SHOW_LIFESTYLE_QUERY,
    initialFilters: cachedLifestylesFilters,
    initialLimit,
    initialSort: cachedLifestylesSort,
    query: FETCH_LIFESTYLES_QUERY,
    trackTotalHits: true
  });

  const {
    lifestylesSet,
    lifestylesSetError,
    lifestylesSetFetched,
    isOptimisticUpdateData,
    lifestylesSetIsPlaceholderData,
    updateLifestylesSetCache,
    isSelectedLifestylesSidebarOpen,
    handleSelectedLifestylesSidebarOpen,
    handleSelectedLifestylesSidebarClose
  } = useLifestylesIndexPageSelectedLifestyles({
    lifestylesSetScope,
    lifestylesSetCacheKey,
    onLifestylesSelect
  });

  const { clearCheckedCategories } =
    useThreeDStockLifestylesContentCheckedCategories();

  const handleResetFilterLifestyles = useCallback<() => void>(() => {
    clearCheckedCategories();
    resetCachedLifestylesFiltersAndSort(filterLifestyles, sortLifestyles);
  }, [
    clearCheckedCategories,
    filterLifestyles,
    resetCachedLifestylesFiltersAndSort,
    sortLifestyles
  ]);

  const { changePathQueryFilters } = useChangePathQueryFilters({
    currentTab: StockTabs.LIFESTYLES,
    companyNanoId,
    isMyLibrary,
    onIndexVisit: isModal ? undefined : handleResetFilterLifestyles
  });

  const handleChangeLifestylesFilters =
    useCallback<ChangeLifestylesFiltersFunc>(
      (changedFilters, removeFilters) => {
        changeCachedLifestylesFilters(changedFilters, removeFilters);

        changeLifestylesFilters(changedFilters, removeFilters);

        if (
          !isEmpty(removeFilters) &&
          categoriesFilterFields.every((field) =>
            removeFilters?.includes(field)
          )
        ) {
          onChangeFilters?.({});

          !isModal && changePathQueryFilters(StockTabs.LIFESTYLES);
        }
      },
      [
        changeCachedLifestylesFilters,
        changeLifestylesFilters,
        changePathQueryFilters,
        isModal,
        onChangeFilters
      ]
    );

  const handleFilterLifestyles = useCallback<FetchLifestylesFilterLifestyles>(
    (nextFilters) => {
      setCachedLifestylesFilters(nextFilters);

      return filterLifestyles(nextFilters);
    },
    [filterLifestyles, setCachedLifestylesFilters]
  );

  const handleClearFilterLifestyles = useCallback<
    (persistFiltersFields?: FetchLifestylesFilterFields[]) => void
  >(
    (persistFiltersFields) => {
      const persistFilters = reduce(
        persistFiltersFields,
        (result, field) => {
          result[field] = lifestylesFilters?.[field];
          return result;
        },
        { ...defaultInitialFilters }
      );

      return handleFilterLifestyles(persistFilters);
    },
    [defaultInitialFilters, handleFilterLifestyles, lifestylesFilters]
  );

  const changeLifestylesCategoryFilters = useCallback<
    (tab?: StockTabs, changeFilters?: FetchLifestylesFilters) => void
  >(
    (tab, changedFilters) => {
      clearCheckedCategories();

      const searchFilters: FetchLifestylesFilters = {};
      if (lifestylesFilters.nameTags)
        searchFilters.nameTags = lifestylesFilters.nameTags;
      if (lifestylesFilters.searchGlobalByLifestyle) {
        searchFilters.searchGlobalByLifestyle =
          lifestylesFilters.searchGlobalByLifestyle;
      }

      if (tab === StockTabs.LIFESTYLES) {
        handleFilterLifestyles({
          ...defaultInitialFilters,
          ...changedFilters,
          ...searchFilters
        });

        onChangeFilters?.({ ...changedFilters, ...searchFilters });
      }

      if (!isModal) {
        changePathQueryFilters(tab, { ...changedFilters, ...searchFilters });
      }
    },
    [
      changePathQueryFilters,
      clearCheckedCategories,
      defaultInitialFilters,
      handleFilterLifestyles,
      isModal,
      onChangeFilters,
      lifestylesFilters
    ]
  );

  const { handleSearch, onSearchSimilarImage, onSearchSimilarLifestyle } =
    useThreeDStockLifestylesSearch({
      changeLifestylesFilters: handleChangeLifestylesFilters
    });

  return (
    <ThreeDStockShowLayout
      isModal={isModal}
      action={pageAction}
      header={
        <ThreeDStockHeader
          tab={StockTabs.LIFESTYLES}
          withAddButton
          withSimilarImageFilter={currentUser.hasPermissions(
            (isMyLibrary &&
              LifestylesPermissions.READ_SELF_COMPANY_LIFESTYLES_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON) ||
              (companyNanoId &&
                LifestylesPermissions.READ_OTHER_COMPANY_LIFESTYLES_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON) ||
              LifestylesPermissions.READ_LIFESTYLES_AI_CLIP_GLOBAL_SEARCH_FILTER_MODAL_BUTTON
          )}
          disabled={!lifestylesFetched}
          onSearch={handleSearch}
          onSearchSimilarImage={onSearchSimilarImage}
          value={lifestylesFilters.nameTags || ''}
          isLib={!!companyNanoId}
          isMyLibrary={isMyLibrary}
          libHref={
            (isMyLibrary && TeamPath.currentCompanyNewLibraryLifestyles()) ||
            (companyNanoId &&
              TeamPath.companyNewLibraryLifestyles(companyNanoId)) ||
            TeamPath.currentCompanyNewLibraryLifestyles()
          }
          stockHref={
            isMyLibrary || !companyNanoId ? StockPath.newLifestyles() : null
          }
          team={team}
        />
      }
      modalHeader={
        <ThreeDStockModalHeader
          tab={StockTabs.LIFESTYLES}
          withSimilarImageFilter
          disabled={!lifestylesFetched}
          onSearch={handleSearch}
          onSearchSimilarImage={onSearchSimilarImage}
          value={lifestylesFilters.nameTags || ''}
          onCategoriesClick={handleMenuToggle}
        />
      }
      subHeader={
        <ThreeDStockSubHeader
          tab={StockTabs.LIFESTYLES}
          companyNanoId={companyNanoId}
          isMyLibrary={isMyLibrary}
          onAllClick={handleMenuToggle}
          onSelectedItemsSidebarOpen={handleSelectedLifestylesSidebarOpen}
          selectedItemsCount={size(lifestylesSet?.selectedLifestyles)}
        />
      }
      secondaryMenu={
        isOpenMenu ? (
          <ThreeDStockMenu
            showTab={isModal ? StockTabs.LIFESTYLES : null}
            onClose={handleMenuToggle}
            changeItemsCategoryFilters={changeLifestylesCategoryFilters}
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
            isModal={isModal}
            clientIds={defaultInitialFilters?.clientIds}
          />
        ) : null
      }
      sidebar={
        isSelectedLifestylesSidebarOpen ? (
          <SelectedLifestylesSidebar
            lifestylesSet={lifestylesSet}
            lifestylesSetFetched={lifestylesSetFetched}
            lifestylesSetError={lifestylesSetError}
            lifestylesSetIsPlaceholderData={lifestylesSetIsPlaceholderData}
            lifestylesCacheKeys={lifestylesCacheKeys}
            lifestylesSetCacheKeys={lifestylesSetCacheKeys}
            onClose={handleSelectedLifestylesSidebarClose}
            updateLifestylesSetCache={updateLifestylesSetCache}
            withoutBackGround={isModal}
            onCustomAttach={onLifestylesAttach}
            disabledAttach={isOptimisticUpdateData}
          />
        ) : null
      }
    >
      <ThreeDStockLifestylesContent
        lifestylesSort={lifestylesSort}
        lifestylesCacheKey={lifestylesCacheKey}
        lifestylesFilters={lifestylesFilters}
        sortLifestyles={sortLifestyles}
        changeLifestylesFilters={handleChangeLifestylesFilters}
        filterLifestyles={handleFilterLifestyles}
        changeLifestylesCategoryFilters={changeLifestylesCategoryFilters}
        lifestyles={lifestyles}
        onLifestyleEditButtonMouseEnter={prefetchLifestyle}
        lifestylesError={lifestylesError}
        lifestylesFetched={lifestylesFetched}
        lifestylesIsPlaceholderData={lifestylesIsPlaceholderData}
        lifestylesTotalCount={lifestylesTotalCount}
        hasNextLifestylesPage={hasNextLifestylesPage}
        lifestylesPage={lifestylesPage}
        lifestylesLimit={lifestylesLimit}
        loadMoreLifestyles={loadMoreLifestyles}
        lifestylesFetchingNextPage={lifestylesFetchingNextPage}
        lifestylesSet={lifestylesSet}
        updateLifestylesSetCache={updateLifestylesSetCache}
        lifestylesSetCacheKeys={lifestylesSetCacheKeys}
        updateLifestyleCache={updateLifestyleCache}
        clearLifestylesFilters={handleClearFilterLifestyles}
        defaultLifestyleCategoryId={defaultInitialFilters?.lifestyleCategoryId}
        defaultLifestyleParentCategoryIds={
          defaultInitialFilters?.lifestyleParentCategoryIds
        }
        companyNanoId={companyNanoId}
        teamUuid={companyUuid}
        isMyLibrary={isMyLibrary}
        withPremiumContentAtFilter={currentUser.hasPermissions(
          (isMyLibrary &&
            LifestylesPermissions.READ_LIFESTYLES_LIBRARY_PREMIUM_CONTENT_AT_FILTER) ||
            (companyNanoId &&
              LifestylesPermissions.READ_LIFESTYLES_COMPANY_LIBRARY_PREMIUM_CONTENT_AT_FILTER) ||
            (isModal &&
              LifestylesPermissions.READ_SELECT_LIFESTYLES_MODAL_BUTTON_PREMIUM_CONTENT_AT_FILTER) ||
            LifestylesPermissions.READ_LIFESTYLES_PREMIUM_CONTENT_AT_FILTER
        )}
        withRealAspectRatio={withRealAspectRatio}
        isModal={isModal}
        onLifestyleSimilarSearchFilter={onSearchSimilarLifestyle}
      />

      {isModal &&
      lifestylesSetFetched &&
      lifestylesSet &&
      !isSelectedLifestylesSidebarOpen ? (
        <SelectedLifestylesFloatingCounter
          lifestylesSet={lifestylesSet}
          lifestylesSetCacheKey={lifestylesSetCacheKey}
          isOpen={isSelectedLifestylesSidebarOpen}
          onOpen={handleSelectedLifestylesSidebarOpen}
          onClose={handleSelectedLifestylesSidebarClose}
          updateLifestylesSetCache={updateLifestylesSetCache}
        />
      ) : null}
    </ThreeDStockShowLayout>
  );
}

export default ThreeDStockLifestyles;
